import React from 'react';
import { PropTypes } from 'prop-types';
import { useTheme } from '@emotion/react';
import { useNavigate } from 'react-router-dom';
import { formatRoute } from 'react-router-named-routes';
import Icon from '@mdi/react';
import { mdiCheckOutline } from '@mdi/js';
import {
  Card,
  CardContent,
  CardMedia,
  Grid,
  IconButton,
  Tooltip,
} from '@mui/material';
// import contentEntryPlaceholder from '../../assets/images/content_entry_placeholder.png';
import { MY_DAY_CONTENT_ENTRY_ROUTE } from '../../services/routes';
import { useAddUserMyDayActionRecordMutation } from '../../app/smorgAppSyncGraphQLApi';
import { contentEntryWatchedActionRecord } from '../../services/my_day';
import {
  trackContentClick,
  useTracker,
} from '../../operations/tracking_operations';
import {
  useCurrentProgrammeEnrollmentQuery,
  useGetContentEntryByIDAndParentQuery,
  useGetCurrentSpaceMembershipQuery,
  useGetSharedContentEntryByIDAndParentQuery,
  useGetUserProfileQuery,
} from '../../app/smorgAppSyncGraphQLApiCustomHooks';
import MyDayPlaceholderCard from './my_day_placeholder_card';

const MyDayContentCard = ({
  contentEntryID,
  parentID,
  calendarDay,
  showMarkAsCompleted,
}) => {
  const navigate = useNavigate();

  const tracker = useTracker();

  const theme = useTheme();

  const { data: userProfile } = useGetUserProfileQuery();

  const { data: currentSpaceMembership } = useGetCurrentSpaceMembershipQuery();

  const { data: currentProgrammeEnrollment } =
    useCurrentProgrammeEnrollmentQuery();

  const { data: ownContentEntry, isSuccess: isOwnContentEntrySuccess } =
    useGetContentEntryByIDAndParentQuery(
      { id: contentEntryID, parentID },
      { skip: !contentEntryID || !parentID },
    );

  const { data: sharedContentEntry, isSuccess: isSharedContentEntrySuccess } =
    useGetSharedContentEntryByIDAndParentQuery(
      { id: contentEntryID, parentID },
      { skip: !contentEntryID || !parentID },
    );

  const contentEntry = ownContentEntry || sharedContentEntry;

  const contentEntryExists = !!contentEntry;

  const title = contentEntry?.title;

  const coverImageUrl = contentEntry?.coverImageUrl;

  const [addUserMyDayActionRecord] = useAddUserMyDayActionRecordMutation();

  const onClickMarkAsWatched = () => {
    addUserMyDayActionRecord(
      contentEntryWatchedActionRecord(
        currentSpaceMembership?.id,
        currentProgrammeEnrollment?.id,
        contentEntryID,
        parentID,
        calendarDay,
      ),
    );
  };

  const handleClick = (ev) => {
    ev.stopPropagation();
    trackContentClick(
      tracker,
      userProfile,
      contentEntryID,
      title,
      null,
      'My Day',
    );
    navigate(
      formatRoute(MY_DAY_CONTENT_ENTRY_ROUTE, {
        parentID,
        sharedContentEntryID: contentEntryID,
      }),
    );
  };

  const hasLoadedSuccessfully =
    isSharedContentEntrySuccess && isOwnContentEntrySuccess;

  if (hasLoadedSuccessfully && !contentEntryExists) {
    return null;
  }

  return !hasLoadedSuccessfully ? (
    <MyDayPlaceholderCard />
  ) : (
    <Card
      className="my-day-card"
      onClick={handleClick}
      sx={{ display: 'flex', flexDirection: 'column' }}
      style={{ backgroundColor: theme.palette.primary.cardBackground }}>
      <Grid item xs={12}>
        <CardMedia
          component="img"
          className="myDayContentEntryCardImage"
          image={coverImageUrl}
          alt=""
        />
      </Grid>
      <Grid item xs={12}>
        <Grid container>
          <Grid item xs={10}>
            <CardContent
              sx={{
                flex: '1 0 auto',
                margin: '8px',
                padding: '0 !important',
                fontSize: '14px',
                whiteSpace: 'break-spaces',
              }}
              style={{
                color: 'white',
                lineClamp: 3,
                WebkitLineClamp: 3,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                display: '-webkit-box',
                WebkitBoxOrient: 'vertical',
              }}>
              {title}
            </CardContent>
          </Grid>
          {showMarkAsCompleted && (
            <Grid
              item
              xs={2}
              style={{ backgroundColor: 'white', display: 'flex' }}>
              <Tooltip title="Mark as watched" placement="top-start">
                <IconButton
                  aria-label="unskip"
                  size="small"
                  style={{ margin: 'auto' }}
                  onClick={(ev) => {
                    onClickMarkAsWatched();
                    ev.stopPropagation();
                  }}>
                  <Icon
                    path={mdiCheckOutline}
                    style={{ width: '24px', height: '24px' }}
                  />
                </IconButton>
              </Tooltip>
            </Grid>
          )}
        </Grid>
      </Grid>
    </Card>
  );
};

MyDayContentCard.propTypes = {
  contentEntryID: PropTypes.string.isRequired,
  parentID: PropTypes.string.isRequired,
  calendarDay: PropTypes.string.isRequired,
  showMarkAsCompleted: PropTypes.bool,
};

MyDayContentCard.defaultProps = {
  showMarkAsCompleted: false,
};

export default MyDayContentCard;
